import { useEffect, useState } from "react";
import moment from "moment";
import { format } from 'date-fns';
import { Button, Image } from "@fluentui/react-components";

import { ResourceSpaceType } from "../../APIService/ResourceSpaceTypeListService";
import { UserBooking } from "../../APIService/UserBookingService";
import { BookingCardAction } from "./work_place";
import { CommonInfo } from "../../CommonData/Config";
import ResourceSpaceList from "../ResourceSpaceType/ResourceSpaceTypeList";
import BookingCard from "./BookingCard";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import { t } from "../../utils/language";

type ExtendBookingModelProps = {
    index: number;
    bookingData: UserBooking;
    action: BookingCardAction;
    extendTimes: { label: string; min: number }[];
    onOptionSelected: (minutes: number) => void;
    onClose: () => void;
};

const backdropStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.3)",
    backdropFilter: "blur(5px)",
    zIndex: 999,
};

const modalStyle: React.CSSProperties = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "235px",
    height: "304px",
    backgroundColor: "#1A1A1A",
    borderRadius: "10px",
    border: "1px solid #525252",
    padding: "10px",
    zIndex: 1000,
    boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
    display: "flex",
    flexDirection: "column",
};

const headerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "#fff",
    marginBottom: "10px",
    fontSize: "16px",
    fontWeight: 600
};

const closeButtonStyle: React.CSSProperties = {
    background: "transparent",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    fontSize: "16px",
};

const ExtendBookingModel = ({
    extendTimes,
    onOptionSelected,
    onClose,
}: ExtendBookingModelProps) => {

    const extendOptions = [
        { label: `30 ${t('Minutes')}`, min: 30 },
        { label: `1 ${t('Hour')}`, min: 60 },
        { label: `1 ${t('Hour')} 30`, min: 90 },
        { label: `2 ${t('Hours')}`, min: 120 },
        { label: `3 ${t('Hours')}`, min: 180 },
        { label: `4 ${t('Hours')}`, min: 240 },
        { label: `6 ${t('Hours')}`, min: 360 },
        { label: `8 ${t('Hours')}`, min: 480 }
    ]

    return ReactDOM.createPortal(
        <>
            {/* Backdrop */}
            <div style={backdropStyle} onClick={onClose} />

            {/* Modal */}
            <div style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "235px",
                height: "304px",
                backgroundColor: "#1A1A1A",
                borderRadius: "10px",
                border: "1px solid #525252",
                padding: "10px",
                zIndex: 1000,
                boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
            }}>
                <div style={{ padding: "10px", gap: "10px" }}>
                    <div style={headerStyle}>
                        <span style={{ fontSize: 16 }}>{t('ExtendDuration')}</span>
                    </div>
                    <div
                        style={{
                            width: "195px",
                            height: "158px",
                            display: "grid",
                            gridTemplateColumns: "92.5px 92.5px",
                            gridTemplateRows: "repeat(4, auto)",   // 4 rows
                            rowGap: "10px",
                            columnGap: "10px",
                            opacity: 1,
                        }}
                    >
                        {extendOptions.map((eq: any) => (
                            <Button
                                key={eq.value}
                                style={{
                                    height: "32px",
                                    borderRadius: "8px",
                                    border: "1px solid #525252",
                                    color: "#fff",
                                    backgroundColor: "transparent",
                                    fontSize: 14,
                                    fontWeight: 500
                                }}
                                onClick={() => {
                                    onOptionSelected(eq.min);
                                    onClose();
                                }}
                            >
                                {eq.label}
                            </Button>
                        ))}
                    </div>
                </div>
                {/* Footer */}
                <div
                    style={{
                        width: "215px",
                        height: "32px",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "5px",
                        padding: "0 10px",
                    }}
                >
                    <Button
                        onClick={onClose}
                        style={{
                            width: "68px",
                            height: "30px",
                            borderRadius: "7px",
                            padding: "6px 16px",
                            gap: "8px",
                            fontSize: 14,
                            fontWeight: 500,
                            border: "1px solid #525252",
                            color: "#fff"
                        }}
                    >
                        {t('Close')}
                    </Button>
                </div>
            </div>
        </>,
        document.body
    );
};

// export default ExtendBookingModel

type UserBookingsProps = {
    theme: string | null;
    dateFormat: string;
    selectedDate: Date;
    resourceSpaceData: ResourceSpaceType[];
    userBookings: UserBooking[];
    onDateChange: (date: Date) => void;
    onSpaceTypeSelect: (item: ResourceSpaceType) => void;
    onBookingCardAction: (index: number, bookingData: UserBooking, action: BookingCardAction) => void;
    onExtendAction: (index: number, bookingData: UserBooking, action: BookingCardAction, min: number) => void;
};

const UserBookings = ({ theme, dateFormat, selectedDate, resourceSpaceData, userBookings, onDateChange, onSpaceTypeSelect, onBookingCardAction, onExtendAction }: UserBookingsProps) => {


    const { t } = useTranslation();
    const [bookingDate, setBookingDate] = useState<Date>(new Date());
    const isToday = moment(bookingDate).isSame(moment(), 'day');
    const [showExtendModel, setShowExtendModel] = useState<boolean>(false);
    const [extendContext, setExtendContext] = useState<{
        index: number;
        bookingData: UserBooking;
        action: BookingCardAction;
    } | null>(null);

    useEffect(() => {
        if (bookingDate !== selectedDate) {
            setBookingDate(selectedDate);
        }
    }, [selectedDate]);

    const handlePrevDay = () => {
        updateDate(moment(bookingDate).subtract(1, "day").toDate());
    };

    const handleNextDay = () => {
        updateDate(moment(bookingDate).add(1, "day").toDate());
    };

    const handleTodayClick = () => {
        updateDate(new Date());
    };

    const updateDate = (newDate: Date) => {
        setBookingDate(newDate);
        onDateChange(newDate);
    };

    const onBookingAction = (index: number, bookingData: UserBooking, action: BookingCardAction) => {
        if (action === BookingCardAction.Extend) {
            console.log("Booking Action: ", action);
            setExtendContext({ index, bookingData, action });
        } else {
            onBookingCardAction(index, bookingData, action);
        }
    };

    const onExtend = (min: number) => {
        if (!extendContext) return;

        const { index, bookingData, action } = extendContext;

        onExtendAction(index, bookingData, action, min);
        setExtendContext(null); // close modal
    };

    return (
        <>
            <div className="col-md-12 ">
                <label className={theme === 'dark' ? 'head_dark' : 'head'}>{t('Bookings')}</label>
                <hr />
            </div>
            <div className="d-flex align-items-center flex-wrap gap-3">
                <div className="d-flex align-items-center" style={{gap: "5px"}}>
                    <img
                        id="button_icon"
                        style={{ width: "20px" }}
                        className='pointer'
                        src={`${CommonInfo.folderName}${theme === 'dark' ? '/Calandarlight.svg' : '/datepicker.svg'}`}
                        alt="Calendar Icon"
                    />
                    <span className={theme === 'dark' ? 'span_head date pointer' : 'date pointer'}>
                        {/* {moment(bookingDate).format(dateFormat ? dateFormat : 'dddd DD MMMM YYYY')} */}
                        {format(bookingDate, "do MMMM yyyy")}
                    </span>
                    <span>
                        <Image onClick={handlePrevDay} className={theme === 'dark' ? 'pointer img_hover' : 'pointer img_hover_light'} src={`${CommonInfo.folderName}${theme === 'dark' ? '/leftarrow/light.svg' : '/leftarrow/dark.svg'}`} style={{ width: "30px", height: "30px", borderRadius: "4px" }} />
                        <Image onClick={handleNextDay} className={theme === 'dark' ? 'pointer img_hover' : 'pointer img_hover_light'} src={`${CommonInfo.folderName}${theme === 'dark' ? '/rightarrow/light.svg' : '/rightarrow/dark.svg'}`} style={{ width: "30px", height: "30px", borderRadius: "4px" }} />
                    </span>
                    {
                        !isToday && (
                            <button className={theme === 'dark' ? 'calendar_btn_dark' : 'calendar_btn'} onClick={handleTodayClick}>
                                <img
                                    id="button_icon"
                                    style={{ width: "18px", height: "18px" }}
                                    className='pointer'
                                    src={`${CommonInfo.folderName}${theme === 'dark' ? '/Calandarlight.svg' : '/datepicker.svg'}`}
                                    alt="Calendar Icon"
                                />
                                {t("Today")}
                            </button>
                        )
                    }
                </div>
                {resourceSpaceData.length > 0 && (
                    <div className='resourceSpaceList'>
                        <ResourceSpaceList theme={theme} resourceSpaceData={resourceSpaceData} onSelect={onSpaceTypeSelect} />
                    </div>
                )}

                {userBookings.map((booking: UserBooking, index: number) => (
                    <BookingCard key={index} booking={booking} theme={theme} onAction={(action) => onBookingAction(index, booking, action)} />
                ))}
                {extendContext && (
                    <ExtendBookingModel
                        index={extendContext.index}
                        bookingData={extendContext.bookingData}
                        action={extendContext.action}
                        extendTimes={[]}
                        onOptionSelected={onExtend}
                        onClose={() => setExtendContext(null)}
                    />
                )}
            </div>

        </>
    )
}

export default UserBookings;